/**
 * Doc pieces the pages, threads and Task detail share (slice 2d spec §8,
 * §10): who changed a doc, what it's linked to, a search's highlight, the
 * doc card and "Updated" line in a thread, and the doc chip on a message.
 */
import { ApiError } from "../../app/api";
import { kindOf } from "../files/kinds";
import { AgentAvatar, AgentChip, PersonAvatar, PersonChip } from "@crew/ui/components/crew-avatar";
import { Link } from "@crew/ui/components/link";
import { StatusDot } from "@crew/ui/components/status";
import { cn } from "@crew/ui/lib/utils";
import { Button } from "@crew/ui/components/button";
import { ArrowUpRight, FileText, X } from "lucide-react";
import type { Me } from "../../app/me";
import { agentTint, initialsOf, personTint } from "../../app/tints";
import { taskStatus } from "../tasks/task-view";
import { useDoc, type DocItem, type DocWho, type Marks } from "./api";
import { cardFooter, fileLine, linkedTo, plain, segments, whoName } from "./doc-view";

/** The doc mark (the boards' file icon), copper as in the card. */
export function DocIcon({
  className,
  file,
}: {
  className?: string;
  /** A file doc's file (slice 5b): its kind's mark in place of the page. */
  file?: { mime: string; name: string } | null;
}) {
  const Icon = file ? kindOf(file).icon : FileText;
  return <Icon aria-hidden className={cn("shrink-0 text-copper", className)} />;
}

/** An avatar for who changed a doc: an agent's glyph, a person's initials. */
export function WhoAvatar({ who, size = 20 }: { who: DocWho; size?: number }) {
  if (who.kind === "agent") {
    return <AgentAvatar glyph={who.glyph ?? "✳"} tint={agentTint(who.tint ?? "ink")} size={size} />;
  }
  const initials = who.initials ?? initialsOf(who.name);
  return <PersonAvatar initials={initials} tint={personTint(who.tint ?? "sand")} size={size} />;
}

/** Avatar and name: "Chief of Staff", "You", "Marcus". */
export function WhoChip({ who, me }: { who: DocWho; me: Me }) {
  const name = whoName(who, me);
  if (who.kind === "agent") {
    return <AgentChip name={name} glyph={who.glyph ?? "✳"} tint={agentTint(who.tint ?? "ink")} />;
  }
  return (
    <PersonChip
      name={name}
      initials={who.initials ?? name.slice(0, 2).toUpperCase()}
      tint={personTint(who.tint ?? "sand")}
    />
  );
}

/** A task's stage as its dot, from a link (which has no waiting text). */
export function StageDot({ stage }: { stage: Parameters<typeof taskStatus>[0]["stage"] }) {
  const s = taskStatus({ stage, waiting_on_text: null });
  return <StatusDot tone={s.tone} hollow={s.hollow} />;
}

/** The list's Linked to (§10.2): a task with its dot, "Chat · …", "Routine · …", "+2". */
export function LinkedTo({
  item,
  className,
}: {
  item: Pick<DocItem, "links">;
  className?: string;
}) {
  const to = linkedTo(item.links);
  if (to.kind === "none") {
    return <span className={cn("text-hint", className)}>{to.label}</span>;
  }
  return (
    <span data-linked-to className={cn("flex min-w-0 items-center gap-2", className)}>
      {to.kind === "task" && to.stage ? (
        <StageDot stage={to.stage} />
      ) : (
        <span aria-hidden className="size-2 shrink-0 rounded-[2px] border border-hint" />
      )}
      <span className="truncate">{to.label}</span>
      {to.more > 0 ? <span className="shrink-0 text-hint">+{to.more}</span> : null}
    </span>
  );
}

/** Text with a search's matches marked (47c). */
export function Highlighted({ text, marks }: { text: string; marks: Marks }) {
  if (marks.length === 0) return <>{text}</>;
  return (
    <>
      {segments(text, marks).map((s, i) =>
        s.mark ? (
          <mark
            key={i}
            data-match
            className="rounded-[4px] bg-copper/15 px-0.5 text-foreground ring-1 ring-copper/10"
          >
            {s.text}
          </mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

/**
 * The doc card in a thread (§8, board 50): its title, first lines, who changed
 * it and when, its task, and Open. Live from the thread's `docs`; a doc no
 * longer linked to the thread (its chat unlinked) is read by id instead, so the
 * card still follows renames and Archive (Codex on #275). The message's body
 * (the title) until either loads.
 */
export function ThreadDocCard({
  listed,
  docId,
  fallbackTitle,
  size,
  me,
}: {
  /** The doc as the thread's `docs` have it, if they do. */
  listed: DocItem | undefined;
  docId: string;
  fallbackTitle: string;
  size: "md" | "sm";
  me: Me;
}) {
  const fetched = useDoc(docId, !listed);
  const doc = listed ?? fetched.data;
  // Deleted (slice 5b §8, Devin on #435): the card says so, and links nowhere.
  if (!doc && fetched.error instanceof ApiError && fetched.error.status === 404) {
    return (
      <div
        data-slot="doc-card"
        data-doc-id={docId}
        data-doc-deleted
        className="flex w-full max-w-[460px] items-center gap-2 self-start rounded-tile bg-card px-3.5 py-3 text-meta text-muted-foreground shadow-[0_0_0_1px_var(--color-border)]"
      >
        <DocIcon className="size-[15px] opacity-60" />
        <span className="min-w-0 flex-1 truncate line-through">{fallbackTitle}</span>
        <span className="shrink-0">Deleted</span>
      </div>
    );
  }
  // A file doc's card says what the file is (slice 5b spec §9.2).
  const preview = doc ? (fileLine(doc) ?? plain(doc.snippet, doc.title)) : "";
  const sm = size === "sm";
  return (
    <Link
      href={`/docs/${docId}`}
      data-slot="doc-card"
      data-doc-id={docId}
      className="flex w-full max-w-[460px] flex-col gap-2 self-start rounded-tile bg-card px-3.5 py-3 text-left shadow-[0_0_0_1px_var(--color-border),0_4px_12px_rgb(31_42_68/4%)] transition-colors hover:bg-card/80"
    >
      <span className="flex items-center gap-2">
        <DocIcon className="size-[15px]" file={doc?.file} />
        <span
          className={cn(
            "min-w-0 flex-1 font-display text-foreground",
            sm ? "text-[19px] leading-[22px]" : "text-[21px] leading-6",
          )}
        >
          {doc?.title ?? fallbackTitle}
        </span>
      </span>
      {preview ? (
        <span className="line-clamp-3 text-meta leading-[19px] text-copy">{preview}</span>
      ) : null}
      <span className="flex items-center justify-between gap-3 border-t border-foreground/[0.06] pt-2 text-xs">
        <span data-doc-card-footer className="min-w-0 truncate text-muted-foreground">
          {cardFooter(doc, me)}
        </span>
        <span className="flex shrink-0 items-center gap-1 font-medium text-foreground">
          Open
          <ArrowUpRight aria-hidden className="size-3" />
        </span>
      </span>
    </Link>
  );
}

/** "Updated <title>" (§8): a quiet line in the thread, to the doc. */
export function DocUpdatedLine({ docId, text }: { docId: string; text: string }) {
  return (
    <Link
      href={`/docs/${docId}`}
      data-doc-line={docId}
      className="inline-flex items-center gap-1.5 underline-offset-2 hover:text-foreground hover:underline"
    >
      <FileText aria-hidden className="size-3 shrink-0" />
      {text}
    </Link>
  );
}

/** A doc attached to a person's message (§11.4): its chip, never the claim. */
export function AttachedDocChip({ docId, title }: { docId: string; title: string | undefined }) {
  // A thread still starting has no docs yet: the composer's chip read the doc, so it's cached.
  const cached = useDoc(docId, false).data?.title;
  return (
    <Link
      href={`/docs/${docId}`}
      data-attached-doc={docId}
      className="mt-1.5 inline-flex max-w-full items-center gap-1.5 self-start rounded-md bg-card px-2 py-1 text-xs font-medium text-foreground shadow-ring hover:bg-card/80"
    >
      <DocIcon className="size-3" />
      <span className="truncate">{title ?? cached ?? "A doc"}</span>
    </Link>
  );
}

/** The doc's chip in the composer (51b, 51d), with its ×. */
export function ComposerDocChip({ docId, onRemove }: { docId: string; onRemove: () => void }) {
  const doc = useDoc(docId);
  return (
    <span
      data-composer-doc={docId}
      className="inline-flex h-7 max-w-full min-w-0 items-center gap-1.5 rounded-md bg-background/70 pr-1 pl-2 text-xs font-medium text-foreground shadow-ring"
    >
      <DocIcon className="size-3.5" />
      <span className="truncate">{doc.data?.title ?? "A doc"}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Remove the doc"
        onClick={onRemove}
        className="size-5 text-muted-foreground"
      >
        <X />
      </Button>
    </span>
  );
}
